// One exercise-type rule for every list, badge, filter and report (CTF reports spec §1).
// CTF wins over Training, Training over Assessment. The shared vectors in
// chaos-player-poc tests/server/fixtures/exercise-type-vectors.json pin this rule for the server
// and the client alike.

export const EXERCISE_TYPES = Object.freeze({
  CTF: "ctf",
  TRAINING: "training",
  ASSESSMENT: "assessment",
})

export const MIXED_TYPE = "mixed"

export const EXERCISE_TYPE_LABELS = Object.freeze({
  ctf: "CTF",
  training: "Training",
  assessment: "Assessment",
  mixed: "Mixed",
})

// Filter order on every screen: All, then the three types.
export const EXERCISE_TYPE_FILTERS = Object.freeze(["ctf", "training", "assessment"])

const KNOWN = new Set(Object.values(EXERCISE_TYPES))

/**
 * The exercise type of one session or exercise row.
 * - A server-classified `exerciseType` wins (rows from getExercises once the server sends it).
 * - `ctfCoordinator.enabled === true` (strictly) is a CTF; a stored coordinator that is off is
 *   not, even on a CTF scenario.
 * - With no coordinator stored, a CTF scenario (`scenarioInfo.Products === "CTF"`) is a CTF.
 * - `guidanceMode === "guided"` is Training; anything else is Assessment.
 */
export const exerciseTypeOf = (session) => {
  if (!session || typeof session !== "object") return EXERCISE_TYPES.ASSESSMENT
  if (KNOWN.has(session.exerciseType)) return session.exerciseType
  const coordinator = session.ctfCoordinator
  if (coordinator && typeof coordinator === "object") {
    if (coordinator.enabled === true) return EXERCISE_TYPES.CTF
  } else if (session.ctfLive === true || session.scenarioInfo?.Products === "CTF") {
    return EXERCISE_TYPES.CTF
  }
  return session.guidanceMode === "guided" ? EXERCISE_TYPES.TRAINING : EXERCISE_TYPES.ASSESSMENT
}

/** A group's type: its members' shared type, `mixed` when they disagree, Assessment when empty. */
export const groupExerciseType = (memberTypes) => {
  const types = [...new Set((memberTypes ?? []).filter(Boolean))]
  if (!types.length) return EXERCISE_TYPES.ASSESSMENT
  return types.length === 1 ? types[0] : MIXED_TYPE
}

export const isCtfExercise = (session) => exerciseTypeOf(session) === EXERCISE_TYPES.CTF

/** Reads `?type=` — only the three known types; anything else means All. */
export const readTypeFilter = (search) => {
  const value = new URLSearchParams(search ?? "").get("type") ?? ""
  return EXERCISE_TYPE_FILTERS.includes(value) ? value : null
}

/** Does a row pass the Type filter? A mixed group passes any filter one of its members passes. */
export const matchesTypeFilter = ({ row, type, memberTypes = null }) => {
  if (!type) return true
  const rowType = row?.exerciseTypeResolved ?? exerciseTypeOf(row)
  if (rowType === MIXED_TYPE && memberTypes) return memberTypes.includes(type)
  return rowType === type
}

/** Counts per type for the filter's badges (top-level rows only; group members sit under their group). */
export const typeCounts = (rows) => {
  const counts = { all: 0, ctf: 0, training: 0, assessment: 0 }
  for (const row of rows ?? []) {
    counts.all += 1
    const type = row?.exerciseTypeResolved ?? exerciseTypeOf(row)
    if (counts[type] !== undefined) counts[type] += 1
  }
  return counts
}
